import { FiChevronDown, FiTag, FiX, FiSearch } from "react-icons/fi";
import { useState, useRef, useEffect } from "react";

// SelectField component for dropdown fields with search capability
export const SelectField = ({
  id,
  value,
  onChange,
  isDisabled,
  options = [],
  placeholder = "Select an option",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const selectedOption = options.find((option) => option.value === value);

  const filteredOptions = searchTerm
    ? options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : options;

  const handleOptionClick = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
        className={`w-full p-3 border ${isOpen ? "border-primary ring-2 ring-primary/20" : "border-gray-300"} rounded-lg bg-white text-left flex items-center justify-between transition-all duration-200 ${
          isDisabled
            ? "bg-gray-50 text-gray-500 cursor-not-allowed"
            : "hover:border-primary/70 text-gray-900"
        } shadow-sm`}
        disabled={isDisabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        id={id}
      >
        <span
          className={`truncate ${!selectedOption ? "text-gray-400" : "font-medium"}`}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <FiChevronDown
          className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isOpen ? "transform rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div
          className="fixed inset-0 z-[999] bg-transparent"
          onClick={() => setIsOpen(false)}
        >
          <div
            className="absolute z-[1000] mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden animate-fadeIn"
            style={{
              width: dropdownRef.current
                ? dropdownRef.current.offsetWidth
                : "auto",
              top: dropdownRef.current
                ? dropdownRef.current.getBoundingClientRect().bottom +
                  window.scrollY
                : 0,
              left: dropdownRef.current
                ? dropdownRef.current.getBoundingClientRect().left +
                  window.scrollX
                : 0,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <FiSearch className="w-4 h-4 text-gray-400" />
                </div>
                <input
                  ref={inputRef}
                  type="text"
                  className="w-full py-2 pl-10 pr-3 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  placeholder="Search options..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>

            <ul
              className="max-h-60 overflow-y-auto py-1 text-base"
              role="listbox"
              aria-labelledby={id}
            >
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <li
                    key={option.value}
                    onClick={() =>
                      !option.disabled && handleOptionClick(option.value)
                    }
                    className={`px-3 py-2.5 cursor-pointer flex items-center text-sm ${
                      option.value === value
                        ? "bg-primary/10 text-primary font-medium"
                        : "text-gray-700 hover:bg-gray-50"
                    } ${option.disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                    role="option"
                    aria-selected={option.value === value}
                  >
                    {option.value === value && (
                      <svg
                        className="w-4 h-4 mr-2 text-primary flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                    <span
                      className={`truncate ${option.value === value ? "" : "ml-6"}`}
                    >
                      {option.label}
                    </span>
                  </li>
                ))
              ) : (
                <li className="px-3 py-3 text-sm text-gray-500 text-center">
                  No options found
                </li>
              )}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

// ToggleField component for boolean switches
export const ToggleField = ({
  id,
  value,
  onChange,
  isDisabled,
  labelOn = "Enabled",
  labelOff = "Disabled",
}) => (
  <div className="w-full p-3 border border-gray-300 rounded-lg bg-white hover:border-primary/50 flex items-center gap-3 transition-colors duration-200 shadow-sm">
    <button
      type="button"
      onClick={() => !isDisabled && onChange(!value)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 ${
        value ? "bg-primary" : "bg-gray-300"
      } ${isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
      role="switch"
      aria-checked={value}
      disabled={isDisabled}
      id={id}
    >
      <span
        className={`w-5 h-5 rounded-full bg-white shadow-md transition-transform duration-300 ${
          value ? "translate-x-[16px]" : "translate-x-0.5"
        }`}
      />
    </button>
    <span
      className={`text-sm ${value ? "text-primary font-medium" : "text-gray-600"}`}
    >
      {value ? labelOn : labelOff}
    </span>
  </div>
);

// TagsField component for tag selection
export const TagsField = ({
  id,
  value = [],
  onChange,
  isDisabled,
  availableTags = [],
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const filteredTags = searchTerm
    ? availableTags.filter(
        (tag) =>
          !value.includes(tag.value) &&
          tag.label.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : availableTags.filter((tag) => !value.includes(tag.value));

  const handleAddTag = (tagValue) => {
    if (!value.includes(tagValue)) {
      onChange([...value, tagValue]);
      setSearchTerm("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    onChange(value.filter((tag) => tag !== tagToRemove));
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className={`flex flex-wrap gap-2 p-3 border ${isOpen ? "border-primary ring-2 ring-primary/20" : "border-gray-300"} rounded-lg min-h-[56px] bg-white transition-all duration-200 ${
          isDisabled ? "bg-gray-50 opacity-75" : "hover:border-primary/70"
        } shadow-sm cursor-text`}
        onClick={() => !isDisabled && setIsOpen(true)}
      >
        {value.length > 0 ? (
          value.map((tag, index) => (
            <span
              key={index}
              className="bg-primary/10 text-primary font-medium px-3 py-1.5 rounded-md text-sm flex items-center group transition-all duration-200 animate-fadeIn"
            >
              <FiTag className="mr-1.5 w-3.5 h-3.5" />
              {tag}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  !isDisabled && handleRemoveTag(tag);
                }}
                className="ml-2 text-primary/70 hover:text-primary hover:bg-primary/20 rounded-full p-1 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed group-hover:bg-primary/10"
                disabled={isDisabled}
                aria-label={`Remove tag ${tag}`}
              >
                <FiX className="h-3 w-3" />
              </button>
            </span>
          ))
        ) : (
          <span className="text-gray-400 text-sm px-1 flex items-center">
            <FiTag className="mr-1.5 opacity-50" />
            No tags selected
          </span>
        )}
      </div>

      {isOpen && (
        <div
          className="fixed inset-0 z-[999] bg-transparent"
          onClick={() => setIsOpen(false)}
        >
          <div
            className="absolute z-[1000] mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden animate-fadeIn"
            style={{
              width: dropdownRef.current
                ? dropdownRef.current.offsetWidth
                : "auto",
              top: dropdownRef.current
                ? dropdownRef.current.getBoundingClientRect().bottom +
                  window.scrollY
                : 0,
              left: dropdownRef.current
                ? dropdownRef.current.getBoundingClientRect().left +
                  window.scrollX
                : 0,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <FiSearch className="w-4 h-4 text-gray-400" />
                </div>
                <input
                  ref={inputRef}
                  type="text"
                  className="w-full py-2 pl-10 pr-3 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  placeholder="Search tags..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>

            <ul className="max-h-60 overflow-y-auto py-1">
              {filteredTags.length > 0 ? (
                filteredTags.map((tag) => (
                  <li
                    key={tag.value}
                    onClick={() => !isDisabled && handleAddTag(tag.value)}
                    className="px-3 py-2.5 cursor-pointer flex items-center text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <FiTag className="mr-2 w-4 h-4 text-primary/70" />
                    <span>{tag.label}</span>
                  </li>
                ))
              ) : (
                <li className="px-3 py-3 text-sm text-gray-500 text-center">
                  {searchTerm
                    ? "No matching tags found"
                    : "No more tags available"}
                </li>
              )}
            </ul>

            {searchTerm &&
              !availableTags.some((t) => t.value === searchTerm) && (
                <div className="p-2 border-t border-gray-100">
                  <button
                    onClick={() => {
                      handleAddTag(searchTerm);
                      setIsOpen(false);
                    }}
                    className="w-full py-2 px-3 text-sm bg-primary/10 text-primary font-medium rounded-md hover:bg-primary/20 transition-colors duration-200 flex items-center justify-center"
                  >
                    <span className="mr-1">+</span> Create tag "{searchTerm}"
                  </button>
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

// SegmentedControlField component for option groups
export const SegmentedControlField = ({
  id,
  value,
  onChange,
  isDisabled,
  options = [],
}) => (
  <div className="flex rounded-lg overflow-hidden border border-gray-300 shadow-sm">
    {options.map((option) => (
      <button
        key={option.value}
        type="button"
        onClick={() => !isDisabled && onChange(option.value)}
        className={`flex-1 py-3 px-4 flex items-center justify-center gap-2 text-sm font-medium ${
          value === option.value
            ? "bg-primary text-white"
            : "bg-white text-gray-700 hover:bg-gray-100"
        } transition-colors duration-200 ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
        disabled={isDisabled}
        id={`${id}-${option.value}`}
      >
        {option.icon && option.icon}
        {option.label}
      </button>
    ))}
  </div>
);
